import EventManager from '../EventManager';
import { afterNextPaint } from '../utils/js';

/**
 * Base 'abstract' DOM scripting controller with common functionality for all
 * other controllers.
 *
 * All child controllers should have an `_init` method, that potentially does
 * custom stuff specifically for that controller, then calls `_initBase`:
 *
 * class SpecialController extends BaseController {
 *   _init(controllerConstructor[, other custom args]) {
 *     [custom stuff here]
 *
 *     // Finish with this, which will call the DOM-ready `init`
 *     this._initBase(controllerConstructor);
 *   }
 * }
 *
 * Should not be exported from js-common, GlobalController should be the least
 * functional controller to use.
 *
 * @private
 */
export default class BaseController extends EventManager {
  /**
   * Controller ID.
   *
   * Must be set in child controllers, preferably to the same as the class name.
   * This is used by the initializer (and possibly others) to check which
   * controller is used, since the actual name will be mangled when minified.
   *
   * @member {?string}
   * @static
   */
  static _baseId = null;

  /**
   * The controller constructor function.
   *
   * @member {Function}
   */
  _controllerConstructor;

  /**
   * In-memory state store.
   *
   * @member {Object}
   */
  _state = {};

  /**
   * Previous state for `didSetState()`.
   *
   * @member {?Object}
   */
  _prevState = null;

  /**
   * Flag to run `didSetState()`.
   *
   * @member {Boolean}
   */
  _hasQueuedUpdate = false;

  /**
   * Set base data.
   *
   * @param {Function} controllerConstructor - The controller class being
   *   instantiated.
   */
  _initBase(controllerConstructor) {
    this._controllerConstructor = controllerConstructor;
    this.init();
  }

  /**
   * Called on DOM ready.
   */
  init() {
    // Override in subclass
  }

  /**
   * Called on window load (all resources have loaded).
   */
  load() {
    // Override in subclass
  }

  /**
   * Called when controller is being destroyed
   *
   * @returns {boolean}
   */
  destroy() {
    // Override in subclass
    return true;
  }

  /* eslint-disable no-unused-vars */
  /**
   * Called after calls to `setState`.
   *
   * @param {object} prevState - The previous state object.
   */
  didSetState(prevState) {
    /* eslint-enable no-unused-vars */
    // Override in subclass
  }

  /**
   * Set new state properties.
   *
   * @param {object} newState - State object that will be merged into the
   *   existing state.
   */
  setState(newState) {
    if (process.env.NODE_ENV === 'development') {
      if (
        !newState ||
        typeof newState !== 'object' ||
        Object.keys(newState).length === 0
      ) {
        throw new Error('The new state must be a plain object');
      }
    }

    // Keep previous state
    if (!this._prevState) {
      this._prevState = Object.assign({}, this.state);
    }

    // Update state
    this._state = Object.assign(this._state, newState);

    // Run update callback when current task queue is done. This will cause
    // multiple setState calls from different places to only trigger a single
    // callback.
    if (!this._hasQueuedUpdate) {
      this._hasQueuedUpdate = true;
      afterNextPaint(() => {
        this.didSetState(this._prevState);
        this._hasQueuedUpdate = false;
        this._prevState = null;
      });
    }
  }

  // eslint-disable-next-line jsdoc/require-param
  /**
   * Prevent the state from being mutated directly.
   *
   * @ignore
   */
  set state(_) {
    throw new Error("Don't set state directly, use setState");
  }

  /**
   * Return the state object.
   *
   * @ignore
   * @returns {object}
   */
  get state() {
    return this._state;
  }
}
